import React, { useEffect, useState } from "react";
import { getCommunity } from "../lib/api";
import { fmtEventDate } from "../lib/eventFormat";
import { MessageSquareQuote, Trophy, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";

export default function Community() {
  const [tab, setTab] = useState("voices");
  const [voices, setVoices] = useState([]);
  const [scores, setScores] = useState([]);

  useEffect(() => {
    getCommunity("voice").then(setVoices).catch(() => {});
    getCommunity("score").then(setScores).catch(() => {});
  }, []);

  return (
    <div className="paper-texture min-h-screen">
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="border-b-2 border-[var(--navy)] pb-4">
          <p className="eyebrow text-[var(--gold)]">Contributed by our neighbors</p>
          <h1 className="font-display text-4xl font-bold text-[var(--navy)] sm:text-5xl">Community Voices</h1>
          <p className="font-serif-body mt-1 max-w-2xl text-slate-600">
            Opinions, essays and game reports submitted by local contributors and coaches. These are community contributions — distinct from reporting by our newsroom staff.
          </p>
        </div>

        <div className="mt-5 flex gap-2">
          <TabBtn active={tab === "voices"} onClick={() => setTab("voices")} icon={MessageSquareQuote} testid="tab-voices">Community Voices</TabBtn>
          <TabBtn active={tab === "scoreboard"} onClick={() => setTab("scoreboard")} icon={Trophy} testid="tab-scoreboard">Scoreboard</TabBtn>
        </div>

        {tab === "voices" ? (
          voices.length === 0 ? <Empty label="No community posts yet. Approved contributor submissions appear here." /> : (
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="voices-list">
              {voices.map((v) => <VoiceCard key={v.id} v={v} />)}
            </div>
          )
        ) : (
          scores.length === 0 ? <Empty label="No game reports yet. Coach-submitted scores appear here once approved." /> : (
            <div className="mt-6 grid gap-5 md:grid-cols-2" data-testid="scoreboard-list">
              {scores.map((s) => <ScoreCard key={s.id} s={s} />)}
            </div>
          )
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[var(--navy)] text-white" : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
    <Icon size={16} /> {children}
  </button>
);

const Empty = ({ label }) => <p className="font-serif-body py-16 text-center text-slate-500">{label}</p>;

function VoiceCard({ v }) {
  return (
    <article data-testid={`voice-${v.id}`} className="card-hover overflow-hidden rounded-xl border border-[var(--paper-border)] bg-white">
      {v.image && <div className="img-zoom h-44 overflow-hidden"><img src={v.image} alt={v.title} className="h-full w-full object-cover" /></div>}
      <div className="p-5">
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--navy)]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--navy)]">
          <Megaphone size={10} /> Community Voice
        </span>
        <h3 className="font-display mt-2 text-xl font-bold text-[var(--navy)]">{v.title}</h3>
        {(v.body || []).slice(0, 2).map((p, i) => <p key={i} className="font-serif-body mt-2 text-sm leading-relaxed text-slate-600">{p}</p>)}
        <p className="font-ui mt-3 text-xs text-slate-500">By <b className="text-[var(--navy)]">{v.author_name}</b> · Community contributor{v.published_at && ` · ${fmtEventDate(v.published_at)}`}</p>
      </div>
    </article>
  );
}

function ScoreCard({ s }) {
  return (
    <article data-testid={`score-${s.id}`} className="overflow-hidden rounded-xl border border-[var(--paper-border)] bg-white">
      <div className="flex items-center justify-between bg-[var(--navy)] px-4 py-2 text-white">
        <span className="font-ui flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"><Trophy size={14} className="text-[var(--gold)]" /> {s.sport || "Game Report"}</span>
        <span className="text-[10px] uppercase tracking-wide text-white/60">Coach report</span>
      </div>
      <div className="grid grid-cols-3 items-center gap-2 border-b border-slate-100 p-4 text-center">
        <div><p className="font-display text-sm font-bold text-[var(--navy)]">{s.home_team}</p><p className="font-display text-3xl font-extrabold text-[var(--navy)]">{s.home_score}</p></div>
        <p className="font-ui text-xs font-bold uppercase text-slate-400">vs</p>
        <div><p className="font-display text-sm font-bold text-[var(--navy)]">{s.away_team}</p><p className="font-display text-3xl font-extrabold text-slate-400">{s.away_score}</p></div>
      </div>
      <div className="p-4">
        <h3 className="font-display text-lg font-bold text-[var(--navy)]">{s.title}</h3>
        {(s.body || []).slice(0, 2).map((p, i) => <p key={i} className="font-serif-body mt-2 text-sm text-slate-600">{p}</p>)}
        {s.standouts && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-slate-700"><b className="text-[var(--gold)]">Standouts:</b> {s.standouts}</p>}
        <p className="font-ui mt-3 text-xs text-slate-500">Filed by <b className="text-[var(--navy)]">{s.author_name}</b></p>
      </div>
    </article>
  );
}
